import { matchPath, useLocation } from 'react-router-dom-v5';
import { Link } from 'react-router-dom-v5';
import { cn } from '@/shared/utils/cn';
import {
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/shared/ui/sidebar';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/shared/ui/tooltip';
import type {
  ExternalItem,
  LinkItem,
  NavigationContext,
} from './navigationConfig';

type LinkOrExternalResolvedItem = (LinkItem | ExternalItem) & {
  state: 'visible' | 'disabled';
};

type NavItemProps = {
  item: LinkOrExternalResolvedItem;
  ctx: NavigationContext;
};

export function NavItem({ item, ctx }: NavItemProps) {
  const { pathname } = useLocation();
  const Icon = item.icon;
  const disabled = item.state === 'disabled';

  const isActive =
    item.kind === 'link' &&
    !!matchPath(pathname, {
      path: item.to,
      exact: item.exact,
    });

  const content = (
    <>
      <Icon />
      <span>{item.label}</span>
    </>
  );

  const buttonClass = cn(
    disabled && 'opacity-50 pointer-events-none',
    isActive && 'font-medium',
  );

  const button =
    item.kind === 'link' ? (
      <SidebarMenuButton
        asChild
        isActive={isActive}
        tooltip={item.label}
        className={buttonClass}
      >
        <Link to={disabled ? pathname : item.to}>
          {content}
        </Link>
      </SidebarMenuButton>
    ) : (
      <SidebarMenuButton
        asChild
        tooltip={item.label}
        className={buttonClass}
      >
        <a
          href={item.href(ctx)}
          target={item.target}
          rel={item.target === '_blank' ? 'noreferrer noopener' : undefined}
        >
          {content}
        </a>
      </SidebarMenuButton>
    );

  if (disabled && item.disabledReason) {
    return (
      <SidebarMenuItem>
        <Tooltip>
          <TooltipTrigger asChild>
            <div>{button}</div>
          </TooltipTrigger>
          <TooltipContent side="right">
            {item.disabledReason}
          </TooltipContent>
        </Tooltip>
      </SidebarMenuItem>
    );
  }

  return <SidebarMenuItem>{button}</SidebarMenuItem>;
}
